import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TabNavigationService } from './tab-navigation.service';

export interface TabOption {
    label: string;
    value: string;
    template?: any;
    data?: any;
}

export interface TabNavigationConfig {
    mainTabName: string;
    tabs: TabOption[];
    onTabChange?: (activeTab: string) => void;
}

@Injectable()
export class TabNavigationHandlerService implements OnDestroy {
    private subscription = new Subscription();
    private config: TabNavigationConfig | null = null;
    private pendingSubTab: string | null = null;

    activeTab: string = '';

    constructor(private tabNavigationService: TabNavigationService) {}

    initialize(config: TabNavigationConfig): void {
        this.config = config;
        this.setupNavigation();
    }

    private setupNavigation(): void {
        if (!this.config) return;

        this.subscription.add(
            this.tabNavigationService.subTab$.subscribe((subTabData) => {
                if (subTabData && subTabData.mainTab === this.config!.mainTabName) {
                    if (this.config!.tabs.length > 0) {
                        // Tabs are loaded, navigate immediately
                        this.navigateToSubTab(subTabData.subTab);
                    } else {
                        // Tabs not loaded yet, store for later
                        this.pendingSubTab = subTabData.subTab;
                    }
                } else if (!subTabData && this.config!.tabs.length > 0) {
                    // Reset to first tab when sub-tab state is cleared
                    this.activeTab = this.config!.tabs[0].value;
                    this.pendingSubTab = null;
                    this.scrollToTop();
                }
            })
        );
    }

    updateTabs(tabs: TabOption[]): void {
        if (!this.config) return;

        this.config.tabs = tabs;

        // Check if there's a pending navigation
        if (this.pendingSubTab) {
            this.navigateToSubTab(this.pendingSubTab);
            this.pendingSubTab = null;
        } else {
            this.activeTab = tabs[0]?.value || '';
        }
    }

    private navigateToSubTab(subTab: string): void {
        if (!this.config) return;

        const targetTab = this.config.tabs.find(
            (tab) => tab.value.toLowerCase() === subTab.toLowerCase()
        );

        if (targetTab) {
            this.activeTab = targetTab.value;
            // Scroll to top after tab change - iOS compatible
            this.scrollToTop();
            
            // Call optional callback
            if (this.config.onTabChange) {
                this.config.onTabChange(this.activeTab);
            }
        } else {
            console.warn(
                `Tab with alias '${subTab}' not found. Available tabs:`,
                this.config.tabs.map((t) => t.value)
            );
        }
    }

    private scrollToTop(): void {
        // Use setTimeout to ensure DOM has updated after tab change
        setTimeout(() => {
            // Multiple approaches for better iOS compatibility
            try {
                // Method 1: Standard scroll behavior
                window.scrollTo({ top: 0, behavior: 'smooth' });
                
                // Method 2: Fallback for iOS Safari
                if (window.scrollY > 0) {
                    document.body.scrollTop = 0; // For Safari
                    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
                }
                
                // Method 3: Additional iOS-specific fix
                const viewport = document.querySelector('meta[name=viewport]');
                if (viewport) {
                    const content = viewport.getAttribute('content');
                    viewport.setAttribute('content', content + ',height=' + window.innerHeight);
                    setTimeout(() => {
                        viewport.setAttribute('content', content || '');
                    }, 100);
                }
            } catch (error) {
                console.warn('Scroll to top failed:', error);
            }
        }, 100);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}